import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms/button";
import { Crescent } from "@/atoms/crescent";
import { Field } from "@/atoms/field";
import { checkPromoCode } from "@/data/checkout";
import { formatRunningMoney, fromCents } from "@/utils/money";

/**
 * The discount code (spec coupon-field 1.2; sheet DS28).
 *
 * One field with Apply inside its trailing edge, enabled after a single character; the crescent
 * takes Apply's place while the server answers, and the answer is one sentence under the field.
 * Once a code is accepted the field is replaced by the applied line: the code in the mono face,
 * what it takes off, whether it only applies to the first charge, and a tertiary Remove that
 * clears it and puts the amount back. The code is upper-cased on display in the mono face,
 * which is what makes an O and a 0 tellable apart when someone reads it out loud.
 *
 * The server answers in English prose, so nothing it says is printed: the message is matched to
 * one of the five `gymkeePay.checkout.promoError.*` sentences that already exist in the store,
 * and anything unrecognised falls to VALIDATION_FAILED rather than putting an English sentence
 * on a French page.
 */

export interface AppliedPromo {
  code: string;
  /** The total after the code, in cents, as the server computed it. */
  discountedAmountCents: number;
  originalAmountCents: number;
  /** A discount that only takes off the first charge says so under the field. */
  firstChargeOnly: boolean;
}

export interface CouponFieldProps {
  paymentLinkId: string;
  currency: string;
  applied: AppliedPromo | null;
  onApplied: (promo: AppliedPromo | null) => void;
  /** A code that arrived in the URL fills the field so a person only has to press Apply. */
  initialCode?: string;
}

export function CouponField({
  paymentLinkId,
  currency,
  applied,
  onApplied,
  initialCode = "",
}: CouponFieldProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialCode);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (applied) {
    const takenOff = fromCents(
      applied.originalAmountCents - applied.discountedAmountCents,
    );
    return (
      <div className="gap-gk-2 flex flex-col">
        <span className="ds-label">{t("gymkeePay.checkout.discountCode")}</span>
        <div className="gap-gk-3 flex items-center justify-between">
          <p className="min-w-0 text-[14px] text-[var(--gk-text-primary)]">
            {t("gymkeePay.checkout.codeApplied", {
              code: applied.code,
              amount: formatRunningMoney(takenOff, currency),
            })}
          </p>
          <Button
            variant="tertiary"
            className="ds-pay-hand flex-none"
            onClick={() => {
              onApplied(null);
              setCode("");
              setError(null);
            }}
          >
            {t("gymkeePay.checkout.removeCode")}
          </Button>
        </div>
        {applied.firstChargeOnly && (
          <p className="ds-helper">
            {t("gymkeePay.checkout.codeFirstChargeOnly")}
          </p>
        )}
      </div>
    );
  }

  const apply = async () => {
    const trimmed = code.trim();
    if (!trimmed || checking) return;
    setChecking(true);
    setError(null);
    try {
      const result = await checkPromoCode(paymentLinkId, trimmed, currency);
      onApplied({
        code: trimmed,
        discountedAmountCents: result.discountedAmount,
        originalAmountCents: result.originalAmount,
        /* Backend gap, not guessed: `check-promo` returns the coupon's percent or amount and
           NOT its `duration`, so nothing in the payload says whether the code takes off the
           first charge or every one. The spec's "first charge only" line is therefore not
           drawn: a sentence about how long a discount lasts, invented from the discount's
           type, would be a claim about somebody's money that the data does not support. */
        firstChargeOnly: false,
      });
    } catch (thrown) {
      setError(t(promoErrorKey(thrown)));
    } finally {
      setChecking(false);
    }
  };

  return (
    <Field
      label={t("gymkeePay.checkout.discountCode")}
      value={code}
      onChange={(event) => {
        setCode(event.target.value);
        if (error) setError(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void apply();
        }
      }}
      autoCapitalize="characters"
      autoComplete="off"
      spellCheck={false}
      error={error}
      /* The mono face and nothing else. The spec asks for the code upper-cased on display and
         gate 6.3 bans a CSS uppercase on body-size text; the mono face is the load-bearing half
         of that rule anyway (coupon-field 1.2 doNot 5: it is what makes an O and a 0 tellable
         apart), and upper-casing the VALUE instead would change a code Stripe matches exactly. */
      style={{ fontFamily: "var(--gk-font-mono)" }}
      affix={
        checking ? (
          <span className="ds-field-affix">
            <Crescent size={16} tone="tertiary" delay={0} />
          </span>
        ) : (
          <Button
            variant="tertiary"
            className="ds-pay-hand flex-none"
            disabled={code.trim().length === 0}
            onClick={() => void apply()}
          >
            {t("gymkeePay.checkout.applyCode")}
          </Button>
        )
      }
    />
  );
}

/**
 * The server's prose, matched to a key.
 *
 * Backend gap, recorded rather than worked around: `check-promo` throws `BadRequestError` with
 * an English sentence and no machine-readable code, so this match is on words. A wording change
 * on the server degrades to VALIDATION_FAILED, which is still a true sentence in the reader's
 * language; the fix is a `code` field on the error body.
 */
function promoErrorKey(thrown: unknown): string {
  const message = messageOf(thrown).toLowerCase();
  if (message.includes("not allowed"))
    return "gymkeePay.checkout.promoError.NOT_APPLICABLE";
  if (message.includes("expired"))
    return "gymkeePay.checkout.promoError.EXPIRED";
  if (message.includes("limit") || message.includes("usage")) {
    return "gymkeePay.checkout.promoError.USAGE_LIMIT_REACHED";
  }
  if (message.includes("invalid promotion code configuration")) {
    return "gymkeePay.checkout.promoError.VALIDATION_FAILED";
  }
  if (message.includes("invalid"))
    return "gymkeePay.checkout.promoError.INVALID_CODE";
  return "gymkeePay.checkout.promoError.VALIDATION_FAILED";
}

function messageOf(thrown: unknown): string {
  if (typeof thrown !== "object" || thrown === null) return "";
  const response = (thrown as { response?: { data?: { message?: unknown } } })
    .response;
  const message = response?.data?.message;
  return typeof message === "string" ? message : "";
}
